import { HTTP_INTERCEPTORS,provideHttpClient } from '@angular/common/http';
import { APP_INITIALIZER, ApplicationConfig, importProvidersFrom, inject } from '@angular/core';
import { LuxonDateAdapter } from '@angular/material-luxon-adapter';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { PreloadAllModules, provideRouter, withInMemoryScrolling, withPreloading } from '@angular/router';
import { provideFuse } from '@fuse';
import { provideTransloco, TranslocoService } from '@ngneat/transloco';
import { firstValueFrom } from 'rxjs';
import { appRoutes } from 'app/app.routes';
import { provideAuth } from 'app/core/auth/auth.provider';
import { provideIcons } from 'app/core/icons/icons.provider';
import { mockApiServices } from 'app/mock-api';
import { TranslocoHttpLoader } from './core/transloco/transloco.http-loader';
import { StoreModule } from '@ngrx/store';
import { reducers } from './store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { environment } from 'environments/environment';
import { UserService } from './service/user.service';
import { JwtInterceptor } from './core/helpers/jwtInterceptor.helper';
import { ChannelService } from './service/channel.service';
import { OperationService } from './service/operation.service';
import { OrganizationService } from './service/organization.service';
import { CommonService } from './service/common.service';
import { SyntheticProfileService } from './service/syntheticProfile.service';
import { ProfileService } from './service/profile.service';
import { QueueService } from './service/queue.service';
import { SensiaService } from './service/sensia.service';
import { TelegramService } from './service/telegram.service';
import { AlertService } from './service/alert.service';
import { ClusterService } from './service/cluster.service';
import { ReportService } from './service/report.service';
import { WorkflowService } from './service/workflow.service';
import { StatisticsService } from './service/statistics.service';
import { QueryService } from './service/query.service';
import { AssetService } from './service/asset.service';
import { EventService } from './service/event.service';
import { EwanService } from './service/ewan.service';
import { ThreatService } from './service/threat.service';
import { ForensicService } from './service/forensic.service';
import { WindowManagerService } from './service/window-manager.service';
import { MessageBusService } from './service/message-bus.service';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { TranslocoMatPaginatorIntl } from './core/i18n/mat-paginator-intl';

export const appConfig: ApplicationConfig = {
    providers: [
        UserService,
        ChannelService,
        OperationService,
        OrganizationService,
        CommonService,
        SyntheticProfileService,
        ProfileService,
        QueueService,
        SensiaService,
        TelegramService,
        AlertService,
        ClusterService,
        ReportService,
        StatisticsService,
        WorkflowService,
        QueryService,
        AssetService,
        EventService,
        EwanService,
        ThreatService,
        ForensicService,
        WindowManagerService,
        MessageBusService,
        provideAnimations(),
        provideHttpClient(),
        provideRouter(appRoutes,
            withPreloading(PreloadAllModules),
            withInMemoryScrolling({scrollPositionRestoration: 'enabled'}),
        ),
        importProvidersFrom(
            StoreModule.forRoot(reducers),
                        StoreDevtoolsModule.instrument({ maxAge: 25, logOnly: environment.production })
        ),        
        {
            provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true
        },
                {
            provide : DateAdapter,
            useClass: LuxonDateAdapter,
        },
        {
            provide : MAT_DATE_FORMATS, 
            useValue: {
                parse  : {
                    dateInput: 'D',
                },
                display: {
                    dateInput         : 'DDD',
                    monthYearLabel    : 'LLL yyyy',
                    dateA11yLabel     : 'DD',
                    monthYearA11yLabel: 'LLLL yyyy',
                },
            },
        },

        provideTransloco({
            config: {
                availableLangs      : [
                    {
                        id   : 'en',
                        label: 'English',
                    },
                    {
                        id   : 'es',
                        label: 'Spanish',
                    },
                ],
                defaultLang         : 'es',
                fallbackLang        : 'en',
                reRenderOnLangChange: true,
                prodMode: environment.production,                              
                missingHandler: { logMissingKey: !environment.production },             
            },
            loader: TranslocoHttpLoader,
        }),
        {
                        provide   : APP_INITIALIZER,
            useFactory: () =>
            {
                const translocoService = inject(TranslocoService);
                const defaultLang = translocoService.getDefaultLang();
                translocoService.setActiveLang(defaultLang);

                return () => firstValueFrom(translocoService.load(defaultLang));
            },
            multi     : true,
        },

        provideAuth(),
        provideIcons(),
        provideFuse({
            mockApi: {
                delay   : 0,
                services: mockApiServices,
            },
            fuse   : {
                layout : 'classic',
                scheme : 'light',
                screens: {
                    sm: '600px',
                    md: '960px',
                    lg: '1280px',
                    xl: '1440px',
                },
                theme  : 'theme-default',
                themes : [
                    {
                        id  : 'theme-default',
                        name: 'Default',
                    },
                    {
                        id  : 'theme-brand',
                        name: 'Brand',
                    },
                    {
                        id  : 'theme-teal',
                        name: 'Teal',
                    },
                    {
                        id  : 'theme-rose',
                        name: 'Rose',
                    },
                    {
                        id  : 'theme-purple',
                        name: 'Purple',
                    },
                    {
                        id  : 'theme-amber',
                        name: 'Amber',
                    },
                ],
            },
        }),
        {
        provide: MatPaginatorIntl,
        useClass: TranslocoMatPaginatorIntl,
        },        
    ],
};
